import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Home, Building2, Menu, X, LogOut, LayoutDashboard, Globe, UserCircle } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { feature } = useSettings();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const dashPath = user && (user.role === "admin" || user.role === "subadmin") ? "/admin" : "/broker";
  const showDash = user && (user.role === "broker" || user.role === "owner" || user.role === "admin" || user.role === "subadmin");

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/browse", label: t("nav.browse") },
    ...(feature("home_loans") ? [{ to: "/home-loans", label: "Home Loans" }] : []),
    { to: "/terms", label: t("nav.terms") },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-brand-page/80 border-b border-brand-line">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-xl bg-brand-emerald flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-brand-ink text-lg">{t("brand")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname === l.to ? "bg-brand-emerald text-white" : "text-brand-muted hover:text-brand-emerald hover:bg-secondary"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "en" ? "te" : "en")}
            data-testid="lang-toggle"
            aria-label="Toggle language"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-line text-sm font-medium text-brand-ink hover:border-brand-emerald transition-colors"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "తెలుగు" : "English"}
          </button>

          <div className="hidden lg:flex items-center gap-2">
            {showDash && (
              <Button variant="outline" className="rounded-full" onClick={() => navigate(dashPath)} data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> {t("nav.dashboard")}
              </Button>
            )}
            {user && user.role !== "admin" && user.role !== "subadmin" && (
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/account")} data-testid="nav-account">
                <UserCircle className="w-4 h-4 mr-1.5" /> {t("nav.account")}
              </Button>
            )}
            {user ? (
              <Button className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout">
                <LogOut className="w-4 h-4 mr-1.5" /> {t("nav.logout")}
              </Button>
            ) : (
              <>
                <Button variant="ghost" className="rounded-full" onClick={() => navigate("/login")} data-testid="nav-login">{t("nav.login")}</Button>
                <Button className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover" onClick={() => navigate("/register")} data-testid="nav-register">{t("nav.register")}</Button>
              </>
            )}
          </div>

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-brand-page px-5 py-4 space-y-2" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-brand-ink font-medium" data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}>
              {l.label}
            </Link>
          ))}
          {showDash && <Link to={dashPath} onClick={() => setOpen(false)} className="block py-2 text-brand-emerald font-semibold" data-testid="nav-mobile-dashboard">{t("nav.dashboard")}</Link>}
          {user && user.role !== "admin" && user.role !== "subadmin" && <Link to="/account" onClick={() => setOpen(false)} className="block py-2 text-brand-ink font-medium" data-testid="nav-mobile-account">{t("nav.account")}</Link>}
          <div className="pt-2 flex gap-2">
            {user ? (
              <Button className="rounded-full w-full bg-brand-terracotta" onClick={() => { logout(); setOpen(false); navigate("/"); }} data-testid="nav-mobile-logout">{t("nav.logout")}</Button>
            ) : (
              <>
                <Button variant="outline" className="rounded-full flex-1" onClick={() => { setOpen(false); navigate("/login"); }} data-testid="nav-mobile-login">{t("nav.login")}</Button>
                <Button className="rounded-full flex-1 bg-brand-emerald" onClick={() => { setOpen(false); navigate("/register"); }} data-testid="nav-mobile-register">{t("nav.register")}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
